import { v4 as uuidV4 } from 'uuid'
import appGraphql from 'api/app_graphql'

import {
  UPDATE_DRAFT,
  CREATE_DRAFT,
  STORED_DRAFT,
  FAILED_DRAFT,
  DELETE_DRAFT,
  FAILED_SEND,
} from 'ducks/drafts2/constants'
import { selectDraftById, selectDraftByTicketId } from 'ducks/drafts2/selectors'
import {
  buildNewConversationDraftDefaults,
  isSaveable,
} from 'ducks/drafts2/util'

import {
  oauthTokenSelector,
  selectCurrentFolderId,
  selectCurrentTicketListRouteAction,
} from 'selectors/app'
import { selectRawTicket } from 'selectors/tickets/byId'
import { selectNextTicketIdInList } from 'selectors/ticket_list'
import { selectCurrentTicketSearchQueryObject } from 'selectors/search'
import {
  selectPreferences,
  selectPrefersClassicView,
  selectPrefersOpenFolderOnTicketReply,
  selectPrefersOpenFolderOnNoteAdd,
  selectCurrentUserPrefersAutoadvanceToNextTicket,
} from 'ducks/currentUser/selectors'
import { selectCurrentUserId } from 'ducks/currentUser/selectors/base'

import { doUpdateEditorVisibility } from 'ducks/editor/operations'

import { doSendChangeset } from 'actions/tickets'
import { doAutoRedirect } from 'actions/autoRedirect'
import { doShowSnackbar } from 'actions/snackbar'
import { doOpenTicketPage } from 'actions/pages'
import { startTimer as startUndoTimer } from 'actions/undoSend'

import { messagePayload } from 'optimistic/message'
import { notePayload } from 'optimistic/note'

import { isPromise, runOnNextTick } from 'util/functions'
import windowUnload from 'util/windowUnload'
import { isTwitter, isFacebook } from 'util/ticket'
import { isEmpty } from 'util/objects'
import { isEmpty as isEmptyArray } from 'util/arrays'
import debug from 'util/debug'

import {
  doFollowTicketById,
  doUnfollowTicketById,
  TRACK_TYPE_FOLLOW_TICKET_NOTE,
} from 'ducks/ticketing/operations/ticketFollow'
import { selectShouldSendFollowRequest } from 'ducks/ticketing/selectors/ticketFollow'
import { doFetchCurrentContact } from 'ducks/crm/contacts/operations/fetching'
import { CHANGE_CONVERSATION_CONTACT_SUCCESS } from 'ducks/crm/contacts/types'

const updateDebounces = {}
const postUpdateCallbacks = {}
const sendDebounces = {}
const saveRequests = {}

function doCreateDraft(ticketId, draftType, draftFields) {
  return (dispatch, getState) => {
    const state = getState()
    let draftDefaults
    if (ticketId === 'new') {
      draftDefaults = buildNewConversationDraftDefaults()
    } else {
      const ticket = selectRawTicket(state, ticketId)
      draftDefaults =
        draftType === 'note' ? ticket.noteDefaults : ticket.draftDefaults
    }

    const fields = {
      ...draftDefaults,
      ...draftFields,
      draftType,
    }
    const isOnlyUpdatingRecipients =
      'to' in draftFields && Object.keys(draftFields).length === 1

    if (draftDefaults) {
      fields.defaultsSet = true
      fields.defaultsSetOnCreate = true
    }
    // catch a case when defaults don't set proper "to"
    // except when the "to" field is emptied by the user
    if (
      !isOnlyUpdatingRecipients &&
      fields.draftType === 'reply' &&
      (!fields.to || fields.to.length === 0)
    ) {
      delete fields.defaultsSet
      delete fields.defaultsSetOnCreate
      fields.defaultsAbortedOnCreate = true
    }
    const draftId = uuidV4()

    dispatch({
      type: CREATE_DRAFT,
      data: { draftId, ticketId, draftType, fields },
    })
    dispatch(doSendDraftToServer(draftId, ticketId))
  }
}

// this function makes sure that if we ever initialized a draft before having draftDefaults created, we can re-initialize those defaults safely without overriding any changes done by the customer
export function doEnsureDefaults(draftId, draftType, forwarding) {
  return (dispatch, getState) => {
    const state = getState()
    const draft = selectDraftById(state, draftId)
    const ticketId = draft.ticketId

    // only do this once
    if (draft.defaultsSet) {
      debug('defaults already set, aborting')
      return 1
    }

    const ticket = selectRawTicket(state, ticketId)
    if (!ticket || isEmpty(ticket)) {
      draft.defaultsFailReason = 'ticket not found'
      debug('ticket not found')
      return 2
    }
    const draftDefaults =
      draftType === 'note' ? ticket.noteDefaults : ticket.draftDefaults

    // this should never happen
    if (!draftDefaults) {
      draft.defaultsFailReason = 'no draft defaults'
      debug('no draft defaults')
      return 3
    }

    const fields = { ...draft }
    let hasChanges = false

    if (isEmptyArray(fields.to)) {
      fields.to = undefined
    }

    const candidates = [
      'title',
      'mailboxId',
      'assigneeId',
      'assigneeGroupId',
      'state',
      'to',
    ]
    candidates.forEach(field => {
      const defaultValue = draftDefaults[field]
      // ONLY override if not set
      if (fields[field] === undefined && defaultValue) {
        fields[field] = defaultValue
        hasChanges = true
      }

      if (field === 'to' && fields[field] !== undefined && forwarding) {
        // no default 'to' field for forwarded messages
        fields[field] = undefined
        hasChanges = true
      }
    })
    if (!hasChanges) {
      draft.defaultsFailReason = 'no changes'
      debug('no changes, aborting')
      return 4
    }

    // our job is done, we should never do it again
    fields.defaultsSet = true
    fields.defaultsSetOnEnsure = true
    dispatch({
      type: UPDATE_DRAFT,
      data: { draftType, draftId, ticketId, fields },
    })
    // only send to server if already sent before
    if (draft.serverVersion > 0) {
      dispatch(doSendDraftToServer(draftId, ticketId))
    }
    return 0
  }
}

// set cc and bcc to default values
export function doSwitchToReplyAll(ticketId, draftType, draftId) {
  return (dispatch, getState) => {
    const state = getState()
    const { to = [], cc = [], bcc = [] } = selectDraftById(state, draftId) || {}
    const messageId = null
    const ticket = selectRawTicket(state, ticketId)

    if (!ticket || isEmpty(ticket)) {
      debug('ticket not found')
      return 2
    }

    const draftDefaults = ticket.draftDefaults || {}
    const fields = {
      replyType: 'reply-all',
      isForwarding: undefined,
      to: to && (to?.length || 0) > 0 ? [to[0]] : draftDefaults.to,
      cc: cc && (cc?.length || 0) > 0 ? cc : draftDefaults.cc,
      bcc: bcc && (bcc?.length || 0) > 0 ? bcc : draftDefaults.bcc,
    }
    handleDraftChange(dispatch, draftType, draftId, ticketId, messageId, fields)

    return 0
  }
}

// Remove all cc and bcc's
export function doSwitchToDirectReply(ticketId, draftType, draftId) {
  return (dispatch, getState) => {
    const state = getState()
    const { to = [] } = selectDraftById(state, draftId) || {}
    const messageId = null
    const ticket = selectRawTicket(state, ticketId)
    const draftDefaults = ticket.draftDefaults || {}

    if (!ticket || isEmpty(ticket)) {
      debug('ticket not found')
      return 2
    }

    const fields = {
      replyType: 'reply-direct',
      isForwarding: undefined,
      to: to && (to?.length || 0) > 0 ? [to[0]] : draftDefaults.to,
      cc: null,
      bcc: null,
    }
    handleDraftChange(dispatch, draftType, draftId, ticketId, messageId, fields)

    return 0
  }
}

export function doSwitchToForward(ticketId, draftType, draftId) {
  return doUpsertReplyDraft(draftId, draftType, ticketId, null, {
    isForwarding: true,
    to: null,
    cc: null,
    bcc: null,
  })
}

export function doDeleteDraft(ticketId, draftId) {
  return dispatch => {
    dispatch(doDeleteDraftLocally(ticketId, draftId))
    dispatch(doDeleteDraftOnServer(ticketId, draftId))
  }
}

export function doDeleteDraftLocally(ticketId, draftId) {
  if (updateDebounces[draftId]) clearTimeout(updateDebounces[draftId])
  if (sendDebounces[draftId]) clearTimeout(draftId[draftId])
  // eslint-disable-next-line import/no-named-as-default-member
  windowUnload.uninstall()

  return {
    type: DELETE_DRAFT,
    data: { draftId, ticketId },
  }
}

export function doUpsertReplyDraft(
  draftId,
  draftType,
  ticketId,
  messageId,
  fields
) {
  return dispatch => {
    let retVal
    if (draftId) {
      retVal = dispatch({
        type: UPDATE_DRAFT,
        data: { draftType, draftId, ticketId, fields },
      })
    } else {
      retVal = dispatch(doCreateDraft(ticketId, draftType, fields))
    }
    // This works because only the field that are different come through. If you
    // change that, this will start to fetch too often.
    const toEmail = (fields?.to || [])[0]?.email
    if (toEmail) {
      dispatch(doFetchCurrentContact())
    } else if (fields.to?.length === 0) {
      dispatch({
        type: CHANGE_CONVERSATION_CONTACT_SUCCESS,
        payload: {
          conversationChangeContact: {
            conversation: {
              number: 'new',
              contact: { id: undefined },
            },
          },
        },
      })
    }
    return retVal
  }
}

export function doReinstateDraft(draft) {
  return dispatch => {
    dispatch({
      type: UPDATE_DRAFT,
      data: {
        draftType: draft.draftType,
        draftId: draft.id,
        ticketId: draft.ticketId,
        fields: draft,
      },
    })
    dispatch(doSendDraftToServer(draft.id, draft.ticketId))
  }
}

function doDeleteDraftOnServer(ticketId, draftId) {
  return (dispatch, getState) => {
    const state = getState()
    const mutation = `
      mutation DraftDelete(
        $draftId: ID!,
      ) {
        draftDelete( input: {
          draftId: $draftId,
        }) {
          success
        }
      }
    `

    const variables = {
      draftId,
    }

    const token = oauthTokenSelector(state)
    return appGraphql(token, mutation, variables)
  }
}

function doSendDraftToServer(draftId, ticketId) {
  return (dispatch, getState) => {
    const state = getState()
    let draft
    if (!draftId) {
      draft = selectDraftByTicketId(state, ticketId)
    } else {
      draft = selectDraftById(state, draftId)
    }
    if (!draft) return
    if (!isSaveable(draft)) {
      // eslint-disable-next-line import/no-named-as-default-member
      windowUnload.uninstall()
      return
    }

    const mutation = `
      mutation DraftSave(
        $draftId: ID!,
        $conversationId: ID!,
        $version: Int!,
        $payload: String!,
        $draftType: String!,
      ) {
        draftSave( input: {
          draftId: $draftId,
          conversationId: $conversationId,
          version: $version,
          payload: $payload,
          draftType: $draftType,
        }) {
          draftId
          version
          success
          payload
          errors {
            type
          }
        }
      }
    `

    const variables = {
      draftId,
      draftType: draft.draftType,
      conversationId: draft.ticketId,
      version: draft.version,
      payload: JSON.stringify(draft),
    }
    if (!variables.conversationId) return
    if (!variables.draftType) return
    if (!variables.version) return

    const token = oauthTokenSelector(state)
    saveRequests[draftId] = appGraphql(token, mutation, variables)
      .then(response => {
        // eslint-disable-next-line no-console
        console.debug(response)
        const serverVersion = draft.version
        // eslint-disable-next-line import/no-named-as-default-member
        windowUnload.uninstall()
        dispatch({
          type: STORED_DRAFT,
          data: { draftId, serverVersion },
        })
      })
      .catch(() => {
        // eslint-disable-next-line import/no-named-as-default-member
        windowUnload.uninstall()
        dispatch({
          type: FAILED_DRAFT,
          data: { draftId },
        })
      })
  }
}

const sendDraftTimeout = 500
export function debouncedSendDraftToServer(dispatch, draftId, ticketId) {
  if (sendDebounces[draftId]) clearTimeout(sendDebounces[draftId])
  sendDebounces[draftId] = setTimeout(() => {
    dispatch(doSendDraftToServer(draftId, ticketId))
    delete sendDebounces[draftId]
  }, sendDraftTimeout)
}

export function handleDraftChange(
  dispatch,
  draftType,
  draftId,
  ticketId,
  messageId,
  fields
) {
  // eslint-disable-next-line import/no-named-as-default-member
  windowUnload.install(
    'Your draft is not yet saved, closing this window may result in data loss'
  )
  dispatch(doUpsertReplyDraft(draftId, draftType, ticketId, messageId, fields))
  debouncedSendDraftToServer(dispatch, draftId, ticketId)
}

export function debouncedHandleDraftChange(
  dispatch,
  draftType,
  draftId,
  ticketId,
  messageId,
  fields,
  // Only pass old body value for editor content change
  oldBody
) {
  // eslint-disable-next-line import/no-named-as-default-member
  windowUnload.install(
    'Your draft is not yet saved, closing this window may result in data loss'
  )
  const key = draftId
  if (updateDebounces[key]) clearTimeout(updateDebounces[key])
  updateDebounces[key] = setTimeout(() => {
    delete updateDebounces[key]
    const hasOnlyBodyField =
      'body' in fields && Object.keys(fields).length === 1
    const isChanged =
      ![null, undefined].includes(oldBody) && hasOnlyBodyField
        ? oldBody !== fields.body
        : true

    if (isChanged) {
      handleDraftChange(
        dispatch,
        draftType,
        draftId,
        ticketId,
        messageId,
        fields
      )
      if (postUpdateCallbacks[key]) {
        postUpdateCallbacks[key].forEach(callback => callback())
        delete postUpdateCallbacks[key]
      }
    }
  }, 500)
}

const handleFailedMessageSend = dispatch => err => {
  dispatch(
    doShowSnackbar(
      'Oops. Your message could not be sent. Please try again in a moment'
    )
  )
  throw err
}

function isUndoable(draft, ticket) {
  if (!draft) return false
  if (draft.isForwarding && draft.isNote) return false
  if (isFacebook(ticket) || isTwitter(ticket)) return false
  return true
}

export function doSendReplyDraft(draftId, follow) {
  return async (dispatch, getState) => {
    if (updateDebounces[draftId]) {
      postUpdateCallbacks[draftId] = [
        () => dispatch(doSendReplyDraft(draftId, follow)),
      ]
      return
    }
    const state = getState()
    const rawDraft = selectDraftById(state, draftId)
    if (!rawDraft) return

    let ticketId = rawDraft.ticketId
    const isNewTicket = ticketId === 'new'

    const ticket = selectRawTicket(state, ticketId)

    // make conversions for compatibility with doSendChangeset
    const draft = { ...rawDraft }
    draft.assignee = { id: draft.assigneeId }
    if (draft.assigneeId === null) draft.assignee = null
    draft.assigned_group_id = draft.assigneeGroupId
    if (draft.assigneeGroupId === null) draft.assigned_group_id = null
    // ensure that notes use the ticket state when not set
    let isNoteWithoutStateChange = false
    if (draft.isNote) {
      if (draft.state === undefined) draft.state = ticket.state
      if (draft.state === ticket.state) isNoteWithoutStateChange = true
    }
    if (ticket.isTwitterTicket && !draft.isNote) {
      draft.body = `${draft.twitterPrefix} ${draft.body}`
    }

    // for any reply except send & snooze, (i.e. send & close/open), we need to
    // explicitly clear the snooze too.
    if (!draft.snoozeUntil) draft.removeSnooze = true

    // we don't want to show snoozed tickets in the 'mine' inbox
    // we also have to add snoozedById so that it goes into 'snoozed' inbox
    if (draft.snoozeUntil) {
      const userId = selectCurrentUserId(state)
      draft.state = 'closed'
      draft.snoozedById = userId
    }

    // when saving notes without any state changes on a snoozed ticket, make sure we don't unsnooze
    if (isNoteWithoutStateChange && ticket.snoozedUntil) {
      draft.removeSnooze = null
    }

    draft.removeDraftId = rawDraft.id

    const { undo_send_delay: delaySeconds } = selectPreferences(state)
    const changesetId = uuidV4()
    const prefersUndo = delaySeconds
    const undoable = prefersUndo && isUndoable(draft, ticket)
    const nextTicketId = selectNextTicketIdInList(state) || null
    const classicView = selectPrefersClassicView(state)
    const isNote = draft.isNote

    let optimisticData

    if (draft.isNote) {
      optimisticData = notePayload(
        state,
        ticketId,
        draft,
        changesetId,
        selectCurrentFolderId(state),
        selectCurrentTicketSearchQueryObject(state)
      )
    } else {
      optimisticData = messagePayload(
        state,
        ticketId,
        draft,
        changesetId,
        selectCurrentFolderId(state),
        selectCurrentTicketSearchQueryObject(state)
      )
    }

    let action

    if (isNewTicket) {
      action = doSendChangeset(null, draft, { optimisticData, changesetId })
    } else {
      action = doSendChangeset(ticketId, draft, {
        optimisticData,
        changesetId,
        // GR: Our SEND_REPLY reducers will handle (optimistic) deleting, and
        // restoration of drafts (on failure). We dont want/need doSendChangeset
        // to delete drafts for us. I recommend deprecating preserveDraft.
        preserveDraft: true, // disable, so we have full control here.
        // we want control over when to auto-navigate away - chiefly we'd like
        // the user to see the undo send button before it auto-navs away.
        // GR: I also recommend deprecating this.
        // NOTE (mbunsch) this line, when set to true, completely wrecks auto-advance
        // I need to bring it back, especially that now undo send is in a toast
        disableNavigateToFolder: !!classicView, // NOTE (jscheel): Normally sending false, but classicView needs to send true
        disableRefreshPage: true,
      })
    }

    if (!draft.isNote) dispatch(doShowSnackbar('Sending your reply...'))
    dispatch(doDeleteDraftLocally(ticketId, draftId))
    dispatch(doUpdateEditorVisibility(false))
    // We only want to send the draft after its saving request is finished
    // to prevent saving after sending
    if (isPromise(saveRequests[draftId])) {
      // eslint-disable-next-line import/no-named-as-default-member
      windowUnload.install(
        `Your ${app.t(
          'ticket'
        )} is being saved. If you close the window, you might lose your changes`
      )
      await saveRequests[draftId]
    }
    delete saveRequests[draftId]

    const onComplete = () => {
      if (isNewTicket) {
        dispatch(doOpenTicketPage(ticketId))
        return
      }

      if (classicView) {
        const shouldOpenFolderOnReply = selectPrefersOpenFolderOnTicketReply(
          state
        )
        const autoAdvance = selectCurrentUserPrefersAutoadvanceToNextTicket(
          state
        )
        const shouldOpenFolderOnNote = selectPrefersOpenFolderOnNoteAdd(state)
        const shouldOpenFolder = isNote
          ? shouldOpenFolderOnNote
          : shouldOpenFolderOnReply
        const routeAction = selectCurrentTicketListRouteAction(state)
        if (shouldOpenFolder && autoAdvance && routeAction) {
          // NOTE (jscheel): Execute on next cycle so everything else runs before
          // we navigate away.
          runOnNextTick(() => {
            dispatch(routeAction)
          })
        } else if (isNote) {
          // Open ticket page like in the modern view if we don't open folder
          dispatch(doOpenTicketPage(ticketId))
        }
      } else if (isNote) {
        dispatch(doOpenTicketPage(ticketId))
      } else if (nextTicketId && !undoable) {
        dispatch(doAutoRedirect(ticketId, nextTicketId))
      }
    }

    dispatch(action)
      .then(res => {
        if (isNewTicket && res?.ticket?.id) {
          ticketId = res.ticket.id
        }
        if (!undoable && !draft.isNote) {
          dispatch(
            doShowSnackbar('Reply sent', {
              link: `/tickets/${ticketId}`,
              linkText: 'Go back to conversation',
            })
          )
        } else if (!draft.isNote) {
          runOnNextTick(() => {
            dispatch(startUndoTimer(changesetId, ticketId, draft))
          })
        }

        if (
          follow !== undefined &&
          selectShouldSendFollowRequest(state, follow)
        ) {
          const followActionOptions = {
            trackType: TRACK_TYPE_FOLLOW_TICKET_NOTE,
          }
          const followAction = follow
            ? doFollowTicketById(ticketId, followActionOptions)
            : doUnfollowTicketById(ticketId, followActionOptions)
          dispatch(followAction)
        }

        if (isNewTicket) {
          onComplete()
        }

        return Promise.resolve(res)
      })
      .catch(err => {
        dispatch({
          type: FAILED_SEND,
          data: { draftId, draft },
        })
        // At this point, the request failed, so reopen the reply form
        if (ticketId && ticketId !== 'new') {
          dispatch(doOpenTicketPage(ticketId))
        }
        dispatch(doUpdateEditorVisibility(true))
        throw err
      })
      .catch(err => {
        handleFailedMessageSend(dispatch)(err)
      })

    if (!isNewTicket) {
      onComplete()
    }
  }
}
